import { isFilled } from '../reveal-menu/reveal.util';
import { textOf } from '../create-form-summary/create-form-summary.util';
import type { StopCardHeader, StopCardInput } from './route.types';

/** The header title of a stop that has no location yet. */
export const NEW_STOP_TITLE = 'Nieuwe stop';

/** Two-digit rail marker for a zero-based position: 0 -> "01". */
export const stopIndexLabel = (position: number): string => String(position + 1).padStart(2, '0');

const pad = (n: number): string => String(n).padStart(2, '0');

/** "02:00" -> "2u", "01:30" -> "1u 30m"; anything that is not HH:mm (or is zero) -> "". */
export function formatStopDuration(duration: unknown): string {
  const match = typeof duration === 'string' ? /^(\d{1,2}):(\d{2})/.exec(duration) : null;
  if (!match) return '';
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return [hours ? `${hours}u` : '', minutes ? `${minutes}m` : ''].filter(Boolean).join(' ');
}

/**
 * A stop counts as filled when it has a location or an activity. Other defaults (a duration, a
 * seeded arrival time) are not content: such a stop stays open, like a fresh one. A config that
 * seeds an activity therefore starts its stops collapsed.
 */
export function isStopFilled(model: Record<string, unknown> | undefined): boolean {
  return isFilled(model?.['location']) || isFilled(model?.['activity']);
}

/** The text of a stop card header; pure so the wording is testable without rendering. */
export function buildStopCardHeader({ position, model, time, invalid }: StopCardInput): StopCardHeader {
  const clock = time && !isNaN(time.getTime()) ? `${pad(time.getHours())}:${pad(time.getMinutes())}` : '';
  const parts = [
    clock || 'Tijd niet ingesteld',
    textOf(model?.['activity']) || 'Geen activiteit',
    formatStopDuration(model?.['duration']) || 'Geen duur',
  ];
  const volumes = Array.isArray(model?.['volumes']) ? model['volumes'].length : 0;
  const extras = [
    volumes ? `${volumes} ${volumes === 1 ? 'volume' : 'volumes'}` : '',
    // custom-toggle stores the string 'true'; config/API writes may hold a real boolean
    model?.['optional'] === true || model?.['optional'] === 'true' ? 'Optionele stop' : '',
  ].filter(Boolean);
  return {
    index: stopIndexLabel(position),
    title: textOf(model?.['location']) || NEW_STOP_TITLE,
    subline: parts.join(' • '),
    collapsedLine: [...parts, ...extras].join(' • '),
    incomplete: invalid,
  };
}
